import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../../../contexts/AuthContext';
import { Info, MapPin, Mail } from 'lucide-react';
import FieldError from '../../shared/FieldError';
import permissionService from '../../../services/api/permissionService';
import eventService from '../../../services/api/eventService';
import { US_STATES_OPTIONS } from '../../../utils/usStates';

const EmployeeModal = ({ initialData, onSubmit, onCancel, onUnsavedChanges, onLoadingChange }) => {
  const { user } = useUser();
  const initialFormState = useMemo(() => ({
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    employeeNumber: initialData?.employeeNumber || '',
    jobTitle: initialData?.jobTitle || '',
    teamId: initialData?.teamId || '',
    hireDate: initialData?.hireDate ? new Date(initialData.hireDate).toISOString().split('T')[0] : '',
    employmentStatus: initialData?.employmentStatus || 'active',
    employmentType: initialData?.employmentType || 'full_time',
    roleId: initialData?.roleId || '',
    clockPin: '',
    // Phase 8: Pay classification & work arrangement
    payClassification: initialData?.payClassification || 'hourly',
    workArrangement: initialData?.workArrangement || 'on_site',
    remoteWorkState: initialData?.remoteWorkState || '',
    // Location assignments (array of location IDs)
    locationIds: initialData?.locationIds || [],
    primaryLocationId: initialData?.primaryLocationId || '',
    // Platform access invite
    sendInvite: false
  }), [initialData]);

  const [formData, setFormData] = useState(initialFormState);

  const [errors, setErrors] = useState({});
  const [loading, setLoadingState] = useState(false);

  // Wrapper to notify parent of loading state changes
  const setLoading = (isLoading) => {
    setLoadingState(isLoading);
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  };
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [showRoleTooltip, setShowRoleTooltip] = useState(false);
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);

  // Company locations for multi-location assignment
  const [companyLocations, setCompanyLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Track unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return (
      formData.firstName !== initialFormState.firstName ||
      formData.lastName !== initialFormState.lastName ||
      formData.email !== initialFormState.email ||
      formData.phone !== initialFormState.phone ||
      formData.employeeNumber !== initialFormState.employeeNumber ||
      formData.jobTitle !== initialFormState.jobTitle ||
      formData.teamId !== initialFormState.teamId ||
      formData.hireDate !== initialFormState.hireDate ||
      formData.employmentStatus !== initialFormState.employmentStatus ||
      formData.employmentType !== initialFormState.employmentType ||
      formData.roleId !== initialFormState.roleId ||
      formData.clockPin !== initialFormState.clockPin ||
      formData.payClassification !== initialFormState.payClassification ||
      formData.workArrangement !== initialFormState.workArrangement ||
      formData.remoteWorkState !== initialFormState.remoteWorkState ||
      JSON.stringify(formData.locationIds) !== JSON.stringify(initialFormState.locationIds) ||
      formData.primaryLocationId !== initialFormState.primaryLocationId
    );
  }, [formData, initialFormState]);

  // Notify parent of unsaved changes
  useEffect(() => {
    if (onUnsavedChanges) {
      onUnsavedChanges(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChanges]);

  // Fetch available roles on mount (wait for authentication)
  useEffect(() => {
    const fetchRoles = async () => {
      // Wait for authentication before making API calls
      if (!user?.id) {
        return;
      }

      try {
        setLoadingRoles(true);
        const response = await permissionService.getCompanyRoles();
        setAvailableRoles(response.roles || []);
      } catch (error) {
        console.error('Failed to fetch roles:', error);
        setAvailableRoles([]);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [user?.id]);

  // Fetch company locations on mount
  useEffect(() => {
    const fetchLocations = async () => {
      if (!user?.id) {
        return;
      }

      try {
        setLoadingLocations(true);
        const response = await eventService.getLocations();
        setCompanyLocations(response.locations || []);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        setCompanyLocations([]);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [user?.id]);

  // Reset form when initial data changes (e.g., switching between create/edit)
  useEffect(() => {
    setFormData(initialFormState);
  }, [initialFormState]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Handle location checkbox toggle
  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const isSelected = prev.locationIds.includes(locationId);
      let newLocationIds;
      let newPrimaryId = prev.primaryLocationId;

      if (isSelected) {
        // Removing location
        newLocationIds = prev.locationIds.filter(id => id !== locationId);
        // If removing primary, set first remaining as primary
        if (prev.primaryLocationId === locationId) {
          newPrimaryId = newLocationIds[0] || '';
        }
      } else {
        // Adding location
        newLocationIds = [...prev.locationIds, locationId];
        // If first location selected, make it primary
        if (newLocationIds.length === 1) {
          newPrimaryId = locationId;
        }
      }

      return {
        ...prev,
        locationIds: newLocationIds,
        primaryLocationId: newPrimaryId
      };
    });

    // Clear error when locations modified
    if (errors.locationIds) {
      setErrors(prev => ({ ...prev, locationIds: null }));
    }
  };

  // Set primary location
  const handleSetPrimaryLocation = (locationId) => {
    if (formData.locationIds.includes(locationId)) {
      setFormData(prev => ({ ...prev, primaryLocationId: locationId }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Require role selection for new employees
    if (!initialData && !formData.roleId) {
      newErrors.roleId = 'Please select a role for this employee';
    }

    // Validate email is required when sending invite
    if (formData.sendInvite && !formData.email?.trim()) {
      newErrors.email = 'Email is required when sending an invitation';
    }

    // Validate PIN if provided (must be exactly 4 digits) - only for hourly employees
    if (formData.clockPin && !/^\d{4}$/.test(formData.clockPin)) {
      newErrors.clockPin = 'PIN must be exactly 4 digits';
    }

    // Remote work state required for remote/hybrid employees
    if ((formData.workArrangement === 'remote' || formData.workArrangement === 'hybrid') && !formData.remoteWorkState) {
      newErrors.remoteWorkState = 'Remote work state is required for remote/hybrid employees';
    }

    // Location required for on_site/hybrid employees when company has multiple locations
    if (companyLocations.length > 1 &&
        (formData.workArrangement === 'on_site' || formData.workArrangement === 'hybrid') &&
        formData.locationIds.length === 0) {
      newErrors.locationIds = 'Please select at least one work location';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Clean up form data - remove empty strings
      const cleanData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      await onSubmit(cleanData);
    } catch (error) {
      console.error('Form submission error:', error);
      // Error handling is done by parent component
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id="employee-form" onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-brand focus:border-brand ${
                errors.firstName ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter first name"
            />
            <FieldError error={errors.firstName} />
          </div>
          
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-brand focus:border-brand ${
                errors.lastName ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter last name"
            />
            <FieldError error={errors.lastName} />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-brand focus:border-brand ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter email address"
            />
            <FieldError error={errors.email} />
          </div>
          
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>

      {/* Employment Details */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Employment Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              type="text"
              id="jobTitle"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
              placeholder="Enter job title"
            />
          </div>
          
          <div>
            <label htmlFor="employeeNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Employee Number
            </label>
            <input
              type="text"
              id="employeeNumber"
              name="employeeNumber"
              value={formData.employeeNumber}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
              placeholder="Enter employee number"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label htmlFor="hireDate" className="block text-sm font-medium text-gray-700 mb-1">
              Hire Date
            </label>
            <input
              type="date"
              id="hireDate"
              name="hireDate"
              value={formData.hireDate}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
            />
          </div>
          
          <div>
            <label htmlFor="employmentStatus" className="block text-sm font-medium text-gray-700 mb-1">
              Employment Status
            </label>
            <select
              id="employmentStatus"
              name="employmentStatus"
              value={formData.employmentStatus}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="employmentType" className="block text-sm font-medium text-gray-700 mb-1">
              Employment Type
            </label>
            <select
              id="employmentType"
              name="employmentType"
              value={formData.employmentType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
            >
              <option value="full_time">Full Time</option>
              <option value="part_time">Part Time</option>
              <option value="contract">Contract</option>
            </select>
          </div>
        </div>
      </div>

      {/* Pay Classification & Work Location */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-4">Pay Classification & Work Location</h4>

        {/* Pay Classification */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pay Classification
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="payClassification"
                value="hourly"
                checked={formData.payClassification === 'hourly'}
                onChange={handleChange}
                className="mr-2 text-brand focus:ring-brand"
              />
              <span className="text-sm text-gray-700">Hourly</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="payClassification"
                value="salaried"
                checked={formData.payClassification === 'salaried'}
                onChange={handleChange}
                className="mr-2 text-brand focus:ring-brand"
              />
              <span className="text-sm text-gray-700">Salaried</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="payClassification"
                value="contractor"
                checked={formData.payClassification === 'contractor'}
                onChange={handleChange}
                className="mr-2 text-brand focus:ring-brand"
              />
              <span className="text-sm text-gray-700">Contractor (1099)</span>
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {formData.payClassification === 'hourly' && 'Hourly employees must clock in/out and are eligible for overtime.'}
            {formData.payClassification === 'salaried' && 'Salaried employees appear in the portal but cannot clock in/out.'}
            {formData.payClassification === 'contractor' && 'Contractors are hidden from the time clock portal and do not receive PTO.'}
          </p>
        </div>

        {/* Work Arrangement */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="workArrangement" className="block text-sm font-medium text-gray-700 mb-1">
              Work Arrangement
            </label>
            <select
              id="workArrangement"
              name="workArrangement"
              value={formData.workArrangement}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-brand focus:border-brand"
            >
              <option value="on_site">On Site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {/* Remote Work State - shown for remote/hybrid */}
          {(formData.workArrangement === 'remote' || formData.workArrangement === 'hybrid') && (
            <div>
              <label htmlFor="remoteWorkState" className="block text-sm font-medium text-gray-700 mb-1">
                Remote Work State <span className="text-red-500">*</span>
              </label>
              <select
                id="remoteWorkState"
                name="remoteWorkState"
                value={formData.remoteWorkState}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-brand focus:border-brand ${
                  errors.remoteWorkState ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select state...</option>
                {US_STATES_OPTIONS.map(state => (
                  <option key={state.value} value={state.value}>{state.label}</option>
                ))}
              </select>
              <FieldError error={errors.remoteWorkState} />
              <p className="mt-1 text-xs text-gray-500">
                Required for labor law compliance (overtime, break rules).
              </p>
            </div>
          )}
        </div>

        {/* Work Locations - shown only if company has 2+ locations */}
        {companyLocations.length > 1 && (formData.workArrangement === 'on_site' || formData.workArrangement === 'hybrid') && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Locations {formData.workArrangement === 'on_site' && <span className="text-red-500">*</span>}
              <button
                type="button"
                className="ml-1 inline-flex relative"
                onMouseEnter={() => setShowLocationTooltip(true)}
                onMouseLeave={() => setShowLocationTooltip(false)}
              >
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                {showLocationTooltip && (
                  <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                    <p>Primary location is used as the default when creating shifts for this employee.</p>
                  </div>
                )}
              </button>
            </label>
            <div className={`border rounded-md p-3 space-y-2 ${errors.locationIds ? 'border-red-300' : 'border-gray-300'}`}>
              {loadingLocations ? (
                <p className="text-sm text-gray-500">Loading locations...</p>
              ) : (
                companyLocations.map(location => (
                  <div key={location.id} className="flex items-center justify-between">
                    <label className="flex items-center flex-1">
                      <input
                        type="checkbox"
                        checked={formData.locationIds.includes(location.id)}
                        onChange={() => handleLocationToggle(location.id)}
                        className="mr-2 rounded text-brand focus:ring-brand"
                      />
                      <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-700">
                        {location.name}
                        {location.city && ` (${location.city}, ${location.state})`}
                      </span>
                    </label>
                    {formData.locationIds.includes(location.id) && (
                      <button
                        type="button"
                        onClick={() => handleSetPrimaryLocation(location.id)}
                        className={`text-xs px-2 py-1 rounded ${
                          formData.primaryLocationId === location.id
                            ? 'bg-blue-100 text-blue-700 font-medium'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {formData.primaryLocationId === location.id ? 'Primary' : 'Set Primary'}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            <FieldError error={errors.locationIds} />
          </div>
        )}
      </div>

      {/* Role & Permissions */}
      {!initialData && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Role & Permissions</h4>
          <div>
            <label htmlFor="roleId" className="block text-sm font-medium text-gray-700 mb-1">
              Employee Role <span className="text-red-500">*</span>
              <button
                type="button"
                className="ml-1 inline-flex relative"
                onMouseEnter={() => setShowRoleTooltip(true)}
                onMouseLeave={() => setShowRoleTooltip(false)}
                disabled={loading}
              >
                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                {showRoleTooltip && (
                  <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                    <p>This role determines what actions the employee can perform in the system. You can change this later in the employee's permissions tab.</p>
                  </div>
                )}
              </button>
            </label>
            <select
              id="roleId"
              name="roleId"
              value={formData.roleId}
              onChange={handleChange}
              disabled={loadingRoles}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-brand focus:border-brand transition-colors ${
                errors.roleId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">
                {loadingRoles ? 'Loading roles...' : 'Select a role...'}
              </option>
              {availableRoles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            <FieldError error={errors.roleId} />
          </div>
        </div>
      )}

      {/* Platform Access Invite (new employees only, non-contractors) */}
      {!initialData && formData.payClassification !== 'contractor' && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Platform Access</h4>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.sendInvite}
              onChange={(e) => setFormData(prev => ({ ...prev, sendInvite: e.target.checked }))}
              disabled={!formData.email?.trim()}
              className="mt-0.5 rounded text-brand focus:ring-brand disabled:opacity-50"
            />
            <div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Send invitation email</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formData.email?.trim()
                  ? `An invitation will be sent to ${formData.email} to set up their account.`
                  : 'Enter an email address to enable invitations.'}
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Employee PIN (new employees - hourly and salaried, not contractors) */}
      {!initialData && formData.payClassification !== 'contractor' && (
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">Employee Portal Access</h4>
          <div>
            <label htmlFor="clockPin" className="block text-sm font-medium text-gray-700 mb-1">
              Portal PIN
            </label>
            <input
              type="text"
              id="clockPin"
              name="clockPin"
              value={formData.clockPin}
              onChange={handleChange}
              maxLength={4}
              inputMode="numeric"
              pattern="\d*"
              className={`w-full max-w-[200px] px-3 py-2 border rounded-md text-sm focus:ring-brand focus:border-brand ${
                errors.clockPin ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="4 digit PIN"
            />
            <FieldError error={errors.clockPin} />
            <p className="mt-1 text-xs text-gray-500">
              {formData.payClassification === 'hourly'
                ? 'Used for time clock and employee portal login. Can be set later.'
                : 'Used for employee portal login (PTO requests, pay stubs). Can be set later.'}
            </p>
          </div>
        </div>
      )}
    </form>
  );
};

export default EmployeeModal;